/*
 * Enclosure Finder engine (Catnets AU).
 * Pure functions + data. No DOM, no network. Shared by the prototype, the tests
 * and (later) assets/enclosure-finder.js in the theme. Rules = PLAN.md sections 3 and 5.
 */
(function (root) {
  'use strict';

  // One entry per SIZE. A size lists the products that make it (Classic first, then Premium).
  var SIZES = {
    trek:   { label: 'Trek 1.78m',            L: 1.78, W: 0.9,  H: 0.9,  kg: 5.2, steel: false, bays: 1, legs: 4, cats: 1, products: ['trek-portable-cat-enclosure-1-78m'] },
    tower:  { label: 'Lite Tower',            L: 1.16, W: 1.16, H: 2.3,  kg: 8,   steel: false, bays: 1, legs: 4, cats: 1, products: ['lite-tower-portable-cat-enclosure-2-3m-height'] },
    p12:    { label: '1.2m',                  L: 1.2,  W: 1.2,  H: 1.2,  kg: null, steel: true, bays: 1, legs: 4, cats: 1, products: ['premium-portable-cat-enclosure-1-2m'] },
    lite23: { label: 'Lite 2.3m',             L: 2.3,  W: 1.16, H: 1.16, kg: 8,   steel: false, bays: 1, legs: 4, cats: 2, products: ['lite-portable-cat-enclosure-2-3m'] },
    s18:    { label: '1.8m',                  L: 1.8,  W: 1.8,  H: 1.8,  kg: 20,  steel: true,  bays: 1, legs: 4, cats: 2, products: ['single-size-free-standing', 'premium-portable-cat-enclosure-1-8m'] },
    d36:    { label: 'Double 3.6m',           L: 3.6,  W: 1.8,  H: 1.8,  kg: 35,  steel: true,  bays: 2, legs: 6, cats: 3, products: ['double-size-free-standing', 'premium-portable-cat-enclosure-double-3-6m'] },
    t54:    { label: 'Triple 5.4m',           L: 5.4,  W: 1.8,  H: 1.8,  kg: 47,  steel: true,  bays: 3, legs: 8, cats: 4, products: ['triple-size-free-standing', 'premium-portable-cat-enclosure-triple-5-4m'] },
    quad:   { label: 'Quad 3.6 x 3.6m',       L: 3.6,  W: 3.6,  H: 1.8,  kg: 68,  steel: true,  bays: 4, legs: 9, cats: 4, products: ['quad-3-6m-x-3-6m-freestanding-cat-enclosure'] }
  };

  var RANGE = {
    'trek-portable-cat-enclosure-1-78m': 'lite',
    'lite-tower-portable-cat-enclosure-2-3m-height': 'lite',
    'lite-portable-cat-enclosure-2-3m': 'lite',
    'premium-portable-cat-enclosure-1-2m': 'premium',
    'single-size-free-standing': 'classic',
    'premium-portable-cat-enclosure-1-8m': 'premium',
    'double-size-free-standing': 'classic',
    'premium-portable-cat-enclosure-double-3-6m': 'premium',
    'triple-size-free-standing': 'classic',
    'premium-portable-cat-enclosure-triple-5-4m': 'premium',
    'quad-3-6m-x-3-6m-freestanding-cat-enclosure': 'classic'
  };

  // Preference order, best first. Tie-breaks from PLAN.md section 3 step 4 are baked in:
  // Tower before 1.2m in small square spots, Lite 2.3m for long narrow spots, Quad before Triple.
  var ORDER = {
    1: ['s18', 'lite23', 'tower', 'p12', 'trek'],
    2: ['d36', 's18', 'lite23', 'tower', 'p12', 'trek'],
    3: ['quad', 't54', 'd36', 's18', 'lite23', 'tower', 'p12', 'trek']
  };
  // Travel (Kurt, 26-09-30): Premium range first, it's the easiest to put up and take down.
  // Same size logic as ORDER, but only sizes Premium makes, then the Lite range as the fallback
  // for spots too small for a Premium. Quad is out (Classic only, 68kg).
  var TRAVEL_ORDER = {
    1: ['s18', 'p12', 'lite23', 'trek', 'tower'],
    2: ['d36', 's18', 'p12', 'lite23', 'tower', 'trek'],
    3: ['t54', 'd36', 's18', 'p12', 'lite23', 'tower', 'trek']
  };

  var QUICK_GUESSES = [
    { id: 'small',  label: 'Small, like a balcony or corner', L: 1.5, W: 1.5 },
    { id: 'medium', label: 'Medium, like a courtyard',        L: 3,   W: 2 },
    { id: 'long',   label: 'Long, down the side of the house', L: 6,  W: 2 },
    { id: 'big',    label: 'Big and open, like a lawn',        L: 4,  W: 4 }
  ];

  function fits(size, len, wid) {
    var e = 1e-9;
    return (size.L <= len + e && size.W <= wid + e) || (size.L <= wid + e && size.W <= len + e);
  }

  function allowedByLocation(key, location) {
    var s = SIZES[key];
    if (location === 'travel') return key !== 'quad';
    if (location === 'grass') return key !== 'trek'; // PVC Trek wants a semi-covered spot
    return true;
  }

  function premiumFirst(list) {
    return list.filter(function (h) { return RANGE[h] === 'premium'; })
      .concat(list.filter(function (h) { return RANGE[h] !== 'premium'; }));
  }

  function fmt(n) { return (Math.round(n * 100) / 100).toString(); }

  /*
   * answers = { cats: 1|2|3, location: 'inside'|'grass'|'hard'|'travel', length: m, width: m }
   * returns { status: 'ok'|'nothing-fits'|'invalid', size, products, alsoFits, reasons, note }
   */
  function recommend(answers) {
    var cats = answers.cats >= 3 ? 3 : answers.cats;
    var len = Number(answers.length), wid = Number(answers.width);
    if (!ORDER[cats] || !(len > 0) || !(wid > 0) || !answers.location) return { status: 'invalid' };

    var order = answers.location === 'travel' ? TRAVEL_ORDER[cats] : ORDER[cats];
    var candidates = order.filter(function (k) {
      return allowedByLocation(k, answers.location) && fits(SIZES[k], len, wid);
    });
    if (!candidates.length) {
      return { status: 'nothing-fits', length: len, width: wid,
        smallest: answers.location === 'grass' ? SIZES.tower : SIZES.trek };
    }

    var key = candidates[0];
    var s = SIZES[key];
    var reasons = [];
    reasons.push('Fits your ' + fmt(len) + ' x ' + fmt(wid) + 'm space (it needs ' + fmt(s.L) + ' x ' + fmt(s.W) + 'm)');

    var note = null;
    var wanted = answers.cats >= 3 ? 3 : answers.cats;
    if (s.cats >= wanted) {
      reasons.push(wanted === 1 ? 'Plenty of room for one cat' : wanted === 2 ? 'Room for both cats' : 'Room for three or more cats');
    } else {
      note = 'For ' + (wanted === 3 ? 'three or more cats' : 'two cats') + " we'd normally go bigger, but this is the biggest enclosure that fits your space.";
    }

    var premiumHere = s.products.some(function (h) { return RANGE[h] === 'premium'; });
    if (answers.location === 'travel') {
      reasons.push(premiumHere
        ? 'Easiest to put up and pack down: the frame pin-locks together with no tools and the net straps on and off'
        : 'Weighs about ' + s.kg + 'kg and packs down with no tools');
    }
    else if (answers.location === 'hard') reasons.push('Works on pavers and decks. Add weight bags to hold it down');
    else if (answers.location === 'grass') reasons.push('Stainless steel reinforced net, UV tested for 10 years outdoors');
    else if (key === 'tower') reasons.push('Small footprint, 2.3m of climbing height');
    else reasons.push('No tools needed, the frame slides together');

    return {
      status: 'ok',
      sizeKey: key,
      size: s,
      // Premium card first on every result (Kurt, 26-09-30): the higher price seen first
      // anchors it, so the Classic reads as the step down.
      products: premiumFirst(s.products),
      alsoFits: candidates[1] ? { sizeKey: candidates[1], size: SIZES[candidates[1]], products: premiumFirst(SIZES[candidates[1]].products) } : null,
      reasons: reasons,
      note: note
    };
  }

  // ---------- Accessories (PLAN.md section 5) ----------
  var ACC = {
    sail18:      'premium-shade-sail-to-suit-all-freestanding-enclosures-square',
    sail12:      'premium-shade-sail-to-suit-1-2m-enclosure',
    cover18:     'uv50-waterproof-cover',
    cover12:     'uv50-waterproof-cover-for-1-2m-cat-enclosures',
    coverLite:   'uv50-waterproof-cover-for-lite-2-3m-cat-enclosure',
    floorLite:   'enclosure-flooring-for-lite-portable-enclosures',
    floorP12:    'enclosure-flooring-for-premium-1-2m-freestanding-enclosure',
    floorClassic:'enclosure-flooring-for-classic-freestanding-enclosures',
    floorPremium:'enclosure-flooring-for-premium-freestanding-enclosures',
    grassRoll:   'artificial-cat-grass',
    grassTile:   'artificial-cat-grass-1m-x-1m',
    storage:     'heavy-duty-storage-bag',
    weight:      'cat-enclosure-weight-bag',
    hammockLite: 'cat-hammock-to-suit-1-2m-freestanding-enclosure',
    hammock18:   'cat-hammock-to-suit-all-freestanding-enclosures',
    scratcher:   'skywalks-weatherproof-scratcher',
    tree:        'skywalks-outdoor-cat-tree',
    tunnel:      'tunnel',
    plaque:      'custom-engraved-pvc-name-plaque'
  };

  var GROUPS = [
    { id: 'shade', label: 'Shade and weather' },
    { id: 'floor', label: 'Flooring' },
    { id: 'secure', label: 'Hold it down and store it' },
    { id: 'play', label: 'Play and comfort' },
    { id: 'extras', label: 'Finishing touches' }
  ];

  // "Recommended for your space" is only the shade sail and the hammock (Kurt, 26-09-30):
  // more than that was overwhelming. Sail whenever the enclosure lives outdoors; hammock
  // wherever one is on offer (an extra one on multi-section enclosures, or the Lite range's).
  var RECOMMENDED = {
    inside: ['hammock'],
    grass:  ['sail', 'hammock'],
    hard:   ['sail', 'hammock'],
    travel: ['sail', 'hammock']
  };

  function sizeKeyOf(productHandle) {
    for (var k in SIZES) if (SIZES[k].products.indexOf(productHandle) !== -1) return k;
    return null;
  }

  function freeHammock(productHandle) {
    var r = RANGE[productHandle];
    return r === 'classic' || r === 'premium';
  }

  function accessoriesFor(productHandle, location) {
    var key = sizeKeyOf(productHandle);
    if (!key) return [];
    var s = SIZES[key];
    var range = RANGE[productHandle];
    var lite = range === 'lite';
    var big = key === 's18' || key === 'd36' || key === 't54' || key === 'quad';
    var list = [];
    function add(kind, group, handle, qty, max, why) {
      list.push({ kind: kind, group: group, handle: handle, qty: qty, max: Math.max(max || qty, qty), why: why || null });
    }

    // Shade and weather
    if (big) add('sail', 'shade', ACC.sail18, 1, s.bays, s.bays > 1 ? 'One sail shades one 1.8m section. Add more for full cover.' : null);
    if (key === 'p12' || key === 'lite23' || key === 'tower') add('sail', 'shade', ACC.sail12, 1, 1);
    if (key === 's18') add('cover', 'shade', ACC.cover18, 1, 1);
    if (key === 'p12') add('cover', 'shade', ACC.cover12, 1, 1);
    if (key === 'lite23') add('cover', 'shade', ACC.coverLite, 1, 1);

    // Flooring (never for the Trek) and artificial grass
    if (key === 'lite23') add('floor', 'floor', ACC.floorLite, 2, 2, 'Two 1.2m panels cover the 2.3m floor.');
    if (key === 'tower') add('floor', 'floor', ACC.floorLite, 1, 1);
    if (key === 'p12') add('floor', 'floor', ACC.floorP12, 1, 1);
    if (big) add('floor', 'floor', range === 'premium' ? ACC.floorPremium : ACC.floorClassic, s.bays, s.bays, s.bays > 1 ? 'One 1.8m panel per section.' : null);
    if (big) { var rolls = key === 'quad' ? 3 : s.bays; add('grass', 'floor', ACC.grassRoll, rolls, rolls + 1, 'Cut to fit. ' + rolls + (rolls > 1 ? ' rolls cover' : ' roll covers') + ' the floor.'); }
    else add('grass', 'floor', ACC.grassTile, 2, 4);

    // Hold it down and store it
    add('weight', 'secure', ACC.weight, s.legs, s.legs * 2, 'One per leg. Fill with pebbles or a brick.');
    add('storage', 'secure', ACC.storage, s.bays, s.bays, s.bays > 1 ? 'The frame needs ' + s.bays + ' bags.' : null);

    // Play and comfort
    if (lite) add('hammock', 'play', ACC.hammockLite, 1, 1);
    // One hammock comes free with every Classic and Premium. Multi-section enclosures can
    // take one per 1.8m section, so offer the rest as extras.
    if (s.bays > 1) add('hammock', 'play', ACC.hammock18, 1, s.bays - 1, 'One hammock comes free. Add more so every section has a lounging spot.');
    add('scratcher', 'play', ACC.scratcher, 1, 4);
    add('tree', 'play', ACC.tree, 1, 2);
    if (s.steel) add('tunnel', 'play', ACC.tunnel, 1, 4, 'Cable-ties straight onto the frame.');

    // Finishing touches
    add('plaque', 'extras', ACC.plaque, 1, 3);

    var rec = RECOMMENDED[location] || [];
    list.forEach(function (a) {
      a.recommended = rec.indexOf(a.kind) !== -1;
      // Shade sail gets the strongest push whenever it's recommended (Kurt, 26-09-30):
      // shown first, with a ribbon and its own reason.
      a.highlight = a.recommended && a.kind === 'sail';
      if (a.highlight) a.why = 'Shade on hot days, and keeps the worst of the rain off.' + (a.why ? ' ' + a.why : '');
    });
    list.sort(function (x, y) { return (y.highlight ? 1 : 0) - (x.highlight ? 1 : 0); });
    return list;
  }

  function cartPermalink(domain, items) {
    // items: [{ variantId, qty }]
    return 'https://' + domain + '/cart/' + items.filter(function (i) { return i.qty > 0; })
      .map(function (i) { return i.variantId + ':' + i.qty; }).join(',');
  }

  var api = {
    SIZES: SIZES, RANGE: RANGE, ORDER: ORDER, QUICK_GUESSES: QUICK_GUESSES, GROUPS: GROUPS, ACC: ACC,
    fits: fits, recommend: recommend, accessoriesFor: accessoriesFor, freeHammock: freeHammock,
    sizeKeyOf: sizeKeyOf, cartPermalink: cartPermalink
  };
  if (typeof module !== 'undefined' && module.exports) module.exports = api;
  else root.EnclosureFinder = api;
})(this);
